import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Clock, Moon, Sun, Save, Key, MessageCircle, Check, X, LogOut, Plus, Trash2 } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { Button, Input, Modal, WeeklyDaySelector } from '../components/common';
import { LanguageSelector } from '../components/common/LanguageSelector';

interface WorkShift {
  startTime: string;
  endTime: string;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  timezone: string;
  workShifts: WorkShift[];
  workScheduleDays: number[];
}

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateProfile, updatePassword, checkAuth, isLoading: authLoading, logout } = useAuthStore();
  const { darkMode, toggleDarkMode } = useUIStore();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [telegramCode, setTelegramCode] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    timezone: 'UTC',
    workShifts: [{ startTime: '09:00', endTime: '18:00' }],
    workScheduleDays: [1, 2, 3, 4, 5], // Default: Monday-Friday
  });

  useEffect(() => {
    // Wait for auth check to complete before redirecting
    if (authLoading) {
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    // Handle both new workShifts format and legacy workStartTime/workEndTime
    const shifts = user.workShifts && user.workShifts.length > 0
      ? user.workShifts
      : [{ startTime: '09:00', endTime: '18:00' }];

    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      timezone: user.timezone,
      workShifts: shifts,
      workScheduleDays: user.workScheduleDays || [1, 2, 3, 4, 5],
    });
  }, [user, navigate, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const success = await updateProfile(formData);
      if (success) {
        setMessage({ type: 'success', text: t('profile.success.profileUpdated') });
        await checkAuth();
      } else {
        setMessage({ type: 'error', text: t('profile.error.updateProfile') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.error.updateProfile') });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwords.new !== passwords.confirm) {
      setMessage({ type: 'error', text: t('auth.validation.passwordsDoNotMatch') });
      return;
    }

    if (passwords.new.length < 6) {
      setMessage({ type: 'error', text: t('profile.password.passwordTooShort', { count: 6 }) });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const success = await updatePassword(passwords.current, passwords.new);
      if (success) {
        setMessage({ type: 'success', text: t('profile.success.passwordChanged') });
        setShowPasswordModal(false);
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        setMessage({ type: 'error', text: t('profile.error.changePassword') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.error.changePassword') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateToken = async () => {
    setIsGeneratingToken(true);
    setMessage(null);

    try {
      const response = await fetch('/api/users/telegram/generate-link-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setGeneratedToken(data.data.token);
      } else {
        setMessage({ type: 'error', text: data.error?.message || t('profile.error.generateToken') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.error.generateToken') });
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/users/telegram/unlink', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('profile.success.telegramUnlinked') });
        await checkAuth();
      } else {
        setMessage({ type: 'error', text: data.error?.message || t('profile.error.unlinkTelegram') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.error.unlinkTelegram') });
    } finally {
      setIsLoading(false);
    }
  };

  // Legacy function - keeping for backwards compatibility
  const handleTelegramLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      // The user needs to start a chat with the bot and send the code
      const response = await fetch('/api/users/telegram/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ code: telegramCode }),
      });

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('profile.success.telegramLinked') });
        await checkAuth();
        setShowTelegramModal(false);
        setTelegramCode('');
      } else {
        setMessage({ type: 'error', text: data.error?.message || t('profile.error.linkTelegram') });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.error.linkTelegram') });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('profile.title')}</h1>
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title={t('common.close')}
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Avatar Section with Logout */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold text-2xl">
                {user.firstName[0]}{user.lastName[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                {user.role}
              </span>
            </div>
          </div>
          <button
            onClick={() => logout()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.logout')}
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline">{t('common.logout')}</span>
          </button>
        </div>

        {/* Basic Info */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('profile.sections.personalInformation')}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t('profile.fields.firstName')}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
            <Input
              label={t('profile.fields.lastName')}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>

          <div className="mt-4">
            <Input
              label={t('common.email')}
              type="email"
              value={formData.email}
              disabled
              hint={t('profile.fields.emailHint')}
            />
          </div>
        </div>

        {/* Work Hours */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('profile.sections.workSchedule')}
          </h3>
          
          {/* Work Shifts */}
          <div className="space-y-4 mb-6">
            {formData.workShifts.map((shift, index) => (
              <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <Input
                  label={t('profile.fields.shiftStart', { number: index + 1 })}
                  type="time"
                  value={shift.startTime}
                  disabled={true}
                  hint={index === 0 ? t('profile.fields.workScheduleAdminHint') : undefined}
                  onChange={(e) => {
                    const newShifts = [...formData.workShifts];
                    newShifts[index] = { ...shift, startTime: e.target.value };
                    setFormData({ ...formData, workShifts: newShifts });
                  }}
                />
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      label={t('profile.fields.shiftEnd', { number: index + 1 })}
                      type="time"
                      value={shift.endTime}
                      disabled={true}
                      onChange={(e) => {
                        const newShifts = [...formData.workShifts];
                        newShifts[index] = { ...shift, endTime: e.target.value };
                        setFormData({ ...formData, workShifts: newShifts });
                      }}
                    />
                  </div>
                  {formData.workShifts.length > 1 && (
                    <button
                      type="button"
                      disabled={true}
                      className="mb-1 p-2 text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        const newShifts = formData.workShifts.filter((_, i) => i !== index);
                        setFormData({ ...formData, workShifts: newShifts });
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              disabled={true}
              className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setFormData({
                ...formData,
                workShifts: [...formData.workShifts, { startTime: '09:00', endTime: '17:00' }]
              })}
            >
              <Plus className="w-4 h-4" />
              {t('profile.fields.addShift')}
            </button>
          </div>

          {/* Work Days */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <WeeklyDaySelector
              value={formData.workScheduleDays}
              onChange={(days) => setFormData({ ...formData, workScheduleDays: days })}
              label={t('profile.fields.workDays')}
              description={t('profile.fields.workDaysAdminHint')}
              disabled={true}
              showIcon={false}
            />
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            {darkMode ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            {t('profile.sections.appearance')}
          </h3>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('profile.appearance.darkMode')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('profile.appearance.darkModeDescription')}</p>
            </div>
            <button
              type="button"
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                darkMode ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                darkMode ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Language */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {t('common.language')}
          </h3>
          <LanguageSelector />
        </div>

        {/* Security */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t('profile.sections.security')}
          </h3>
          
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowPasswordModal(true)}
            >{t('profile.password.changePassword')}</Button>
          </div>
        </div>

        {/* Telegram */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            {t('profile.sections.telegram')}
          </h3>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {user.telegramChatId ? t('profile.telegram.statusConnected') : t('profile.telegram.statusNotConnected')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user.telegramChatId
                  ? t('profile.telegram.chatId', { chatId: user.telegramChatId })
                  : t('profile.telegram.linkHint')
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              {user.telegramChatId ? (
                <>
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                    <Check className="w-4 h-4" /> {t('common.active')}
                  </span>
                  <Button variant="danger" size="sm" onClick={() => setShowUnlinkConfirm(true)} isLoading={isLoading}>{t('profile.telegram.unlink')}</Button>
                </>
              ) : (
                <Button variant="secondary" onClick={() => setShowTelegramModal(true)}>{t('profile.telegram.link')}</Button>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" isLoading={isLoading} leftIcon={<Save className="w-4 h-4" />}>{t('common.saveChanges')}</Button>
        </div>
      </form>

      {/* Password Change Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={t('profile.password.changePassword')}
        size="sm"
      >
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <Input
            label={t('profile.password.currentPassword')}
            type="password"
            value={passwords.current}
            onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
            required
          />
          <Input
            label={t('profile.password.newPassword')}
            type="password"
            value={passwords.new}
            onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
            required
          />
          <Input
            label={t('profile.password.confirmNewPassword')}
            type="password"
            value={passwords.confirm}
            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
            required
          />
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="secondary" onClick={() => setShowPasswordModal(false)}>{t('common.cancel')}</Button>
            <Button type="submit" isLoading={isLoading}>{t('profile.password.changePassword')}</Button>
          </div>
        </form>
      </Modal>

      {/* Telegram Link Modal */}
      <Modal
        isOpen={showTelegramModal}
        onClose={() => { 
          setShowTelegramModal(false); 
          setGeneratedToken(''); 
          setTelegramCode('');
        }}
        title={generatedToken ? t('profile.telegram.linkTokenGenerated') : t('profile.telegram.linkAccount')}
        size="sm"
      >
        {generatedToken ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('profile.telegram.tokenGenerated')}
              </p>
            </div>
            
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
              <code className="text-lg font-mono break-all select-all">{generatedToken}</code>
            </div>
            
            <button
              onClick={() => navigator.clipboard.writeText(generatedToken)}
              className="w-full text-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >{t('profile.telegram.copyToken')}</button>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-2 list-decimal list-inside">
                <li>{t('profile.telegram.instructions.step1')}</li>
                <li>{t('profile.telegram.instructions.step2')}</li>
                <li>{t('profile.telegram.instructions.step3')}</li>
              </ol>
            </div>

            <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={() => { setGeneratedToken(''); }}>{t('profile.telegram.generateNewToken')}</Button>
              <Button type="button" variant="primary" onClick={async () => { 
                await checkAuth();
                setShowTelegramModal(false); 
                setGeneratedToken(''); 
              }}>{t('common.done')}</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleTelegramLink} className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <ol className="text-sm text-blue-700 dark:text-blue-300 space-y-2 list-decimal list-inside">
                <li>{t('profile.telegram.instructions.generateStep1')}</li>
                <li>{t('profile.telegram.instructions.generateStep2')}</li>
                <li>{t('profile.telegram.instructions.generateStep3')}</li>
                <li>{t('profile.telegram.instructions.generateStep4')}</li>
              </ol>
            </div>
            
            {!user?.telegramChatId && (
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleGenerateToken}
                isLoading={isGeneratingToken}
                className="w-full"
              >{t('profile.telegram.generateToken')}</Button>
            )}
            
            {user?.telegramChatId && (
              <div className="space-y-4">
                <div className="text-center text-green-600 dark:text-green-400">
                  {t('profile.telegram.alreadyLinked')}
                </div>
                <Button 
                  type="button" 
                  variant="danger"
                  onClick={() => setShowUnlinkConfirm(true)}
                  isLoading={isLoading}
                  className="w-full"
                >{t('profile.telegram.unlink')}</Button>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={() => setShowTelegramModal(false)}>{t('common.cancel')}</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showUnlinkConfirm} onClose={() => setShowUnlinkConfirm(false)} title={t('profile.telegram.unlinkConfirmTitle')} size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t('profile.telegram.unlinkConfirmMessage')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setShowUnlinkConfirm(false)}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => { setShowUnlinkConfirm(false); handleUnlinkTelegram(); }} className="!bg-red-600 hover:!bg-red-700">Unlink</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;